import { useEffect, useMemo, useState } from 'react';

import { Block as BlockIcon, TaskAlt as TaskAltIcon } from '@mui/icons-material';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Button, MenuItem, Select } from '@mui/material';

import { useMutation, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { MerchantStatuses, PartnerStatuses } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { RESEND_EMAIL_INVITE } from 'src/graphql/mutations/login.mutation';
import { SUSPEND_PARTNER, UN_SUSPEND_PARTNER } from 'src/graphql/mutations/partner.mutation';
import { UPDATE_BUSINESS_MUTATION } from 'src/graphql/mutations/updateBusiness.mutation';
import { UPDATE_MERCHANT_DASHBOARD_MUTATION } from 'src/graphql/mutations/updateMerchant.mutation';
import { UPDATE_USER_MUTATION } from 'src/graphql/mutations/updateUser.mutation';
import { GET_BUSINESS_USER } from 'src/graphql/queries/users.query';
import SuspendModal from 'src/pages/ViewPartnerPage/components/SuspendModal/SuspendModal';

import { ConfirmationModal } from '../Modals';
import { showErrorToast, showSuccessToast } from '../Toasts/toast';

interface StatusProps {
    status: string;
    partnerName?: string;
    id: number;
    isMerchant?: boolean;
    businessId?: number;
    refetchPartner?: (
        variables?: Partial<{
            id: string;
        }>
    ) => Promise<ApolloQueryResult<any>>;
}

const Status = ({
    id,
    status,
    partnerName,
    businessId,
    isMerchant,
    refetchPartner,
}: StatusProps) => {
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const { user } = useAuthContext();
    const [currentStatus, setCurrentStatus] = useState(status);
    const [businessData, setBusinessData] = useState(null);
    const [suspendReason, setSuspendReason] = useState(null);

    const [openSuspendModal, setOpenSuspendModal] = useState(false);

    const [suspendPartner] = useMutation(SUSPEND_PARTNER, {
        onCompleted: ({ suspendPartner: { status } }) => {
            showSuccessToast('Partner suspended successfully');
            setCurrentStatus(status);
        },
        onError: (error) => {
            setCurrentStatus(currentStatus);
            showErrorToast(error.message);
        },
    });

    const [unSuspendPartner] = useMutation(UN_SUSPEND_PARTNER, {
        onCompleted: ({ unSuspendPartner: { status } }) => {
            showSuccessToast('Partner unsuspended successfully');
            setCurrentStatus(status);
            setOpenSuspendModal(false);
            refetchPartner();
        },
        onError: (error) => {
            setCurrentStatus(currentStatus);
            showErrorToast(error.message);
        },
    });

    const { data, loading } = useQuery(GET_BUSINESS_USER, {
        variables: { businessId },
        skip: !businessId,
    });

    const [updateBusiness] = useMutation(UPDATE_BUSINESS_MUTATION, {
        onError: () => {
            showErrorToast('Error Changing Partner Status');
        },
    });

    const [updateMerchantDashboard] = useMutation(UPDATE_MERCHANT_DASHBOARD_MUTATION, {
        onError: () => {
            showErrorToast('Error Changing Merchant Status');
        },
    });

    const [updateUser] = useMutation(UPDATE_USER_MUTATION, {
        onError: () => {
            showErrorToast('Error Changing User Status');
        },
    });

    const [resendEmailInvitation] = useMutation(RESEND_EMAIL_INVITE, {
        onCompleted: () => {
            showSuccessToast('Email Sent Successfully');
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Resending Activation Email');
        },
    });

    const statusOptions = useMemo(
        () =>
            Object.values(PartnerStatuses).filter((partnerStatusOption) => {
                const isSuspended = status === PartnerStatuses.SUSPENDED;
                if (isSuspended) {
                    return isSuspended;
                }
                return partnerStatusOption !== PartnerStatuses.SUSPENDED;
            }),
        [status]
    );

    useEffect(() => {
        if (businessId) {
            const { findUserByBusinessId } = data ?? {};
            setBusinessData(findUserByBusinessId);
        }
    }, [businessId, data]);

    const handleStatusChange = async (newStatus) => {
        if (isMerchant) {
            await updateMerchantDashboard({
                variables: {
                    id,
                    input: {
                        status: newStatus,
                    },
                },
            });
        } else {
            const shouldUnsuspend =
                newStatus === PartnerStatuses.ACTIVE && currentStatus === PartnerStatuses.SUSPENDED;
            if (businessId) {
                const { email, username } = user;
                if (shouldUnsuspend) {
                    await unSuspendPartner({
                        variables: {
                            id,
                        },
                    });
                } else {
                    await updateBusiness({
                        variables: {
                            id: businessId,
                            input: {
                                userEmail: email,
                                username,
                                status: newStatus,
                            },
                        },
                    });
                }
            }

            if (businessData && !shouldUnsuspend) {
                await updateUser({
                    variables: {
                        id: businessData?.id,
                        input: {
                            status: newStatus === PartnerStatuses.CANCELLED ? 'LOCKED' : newStatus,
                        },
                    },
                });
            }
        }
        setCurrentStatus(newStatus);
    };

    const resendActivationEmail = () => {
        resendEmailInvitation({ variables: { id: businessData?.id } });
    };

    const handleStatusDropDownChange = async (event: any) => {
        setCurrentStatus(event.target.value);
        handleStatusChange(event.target.value);
    };

    const handleSuspendPartner = () => {
        if (businessId) {
            suspendPartner({
                variables: {
                    id: id,
                    suspendReasonDescription: suspendReason,
                },
            });
            setOpenSuspendModal(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <Box sx={{ display: 'flex', gap: '24px', justifyContent: 'flex-end' }}>
            {/* conditionals */}

            {isMerchant ? (
                <>
                    {currentStatus === MerchantStatuses.CANCELLED ||
                        (currentStatus === MerchantStatuses.INACTIVE && (
                            <Box display="flex" alignItems="center" gap="16px">
                                <StatusChip type="user" status={currentStatus} />
                                <Button
                                    startIcon={<TaskAltIcon />}
                                    variant="npi-primary"
                                    onClick={() => {
                                        handleStatusChange(MerchantStatuses.ACTIVE);
                                    }}
                                >
                                    Activate
                                </Button>
                            </Box>
                        ))}

                    {currentStatus === MerchantStatuses.ACTIVE ||
                    currentStatus === MerchantStatuses.SUSPENDED ? (
                        <Box display="flex" alignItems="center" gap="16px">
                            <Box>
                                <Select
                                    fullWidth
                                    sx={{
                                        width: '136px',
                                        color: '#F18F34',
                                        bgcolor: 'white',
                                        height: '40px',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    value={currentStatus}
                                    onChange={handleStatusDropDownChange}
                                    name="userStatus"
                                >
                                    {Object.values(MerchantStatuses).map((status) => (
                                        <MenuItem
                                            value={status}
                                            key={status}
                                            sx={{ alignSelf: 'center' }}
                                        >
                                            <StatusChip type="user" status={status} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            {currentStatus === MerchantStatuses.ACTIVE ? (
                                <Button
                                    startIcon={<BlockIcon />}
                                    variant="npi-primary"
                                    onClick={() => {
                                        handleStatusChange(MerchantStatuses.SUSPENDED);
                                    }}
                                >
                                    Suspend
                                </Button>
                            ) : null}
                        </Box>
                    ) : null}
                </>
            ) : (
                <>
                    {/** Partner status*/}
                    {currentStatus === PartnerStatuses.CANCELLED && (
                        <Box display="flex" alignItems="center" gap="16px">
                            <StatusChip type="user" status={currentStatus} />

                            <Button
                                startIcon={<TaskAltIcon />}
                                variant="npi-primary"
                                onClick={() => {
                                    if (businessId) {
                                        handleStatusChange(PartnerStatuses.ACTIVE);
                                        if (businessData) {
                                            resendActivationEmail();
                                        }
                                    }
                                }}
                            >
                                Activate
                            </Button>
                        </Box>
                    )}

                    {currentStatus === PartnerStatuses.ACTIVE ||
                    currentStatus === PartnerStatuses.SUSPENDED ? (
                        <Box display="flex" alignItems="center" gap="16px">
                            <Box>
                                <Select
                                    fullWidth
                                    disabled={currentStatus === PartnerStatuses.SUSPENDED}
                                    sx={{
                                        width: '136px',
                                        color: '#F18F34',
                                        bgcolor: 'white',
                                        height: '40px',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    value={currentStatus}
                                    onChange={handleStatusDropDownChange}
                                    name="partnerStatus"
                                >
                                    {statusOptions?.map((status) => (
                                        <MenuItem
                                            value={status}
                                            key={status}
                                            sx={{ alignSelf: 'center' }}
                                        >
                                            <StatusChip type="partner" status={status} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            {(() => {
                                if (currentStatus === PartnerStatuses.ACTIVE) {
                                    return (
                                        <Button
                                            startIcon={<BlockIcon />}
                                            variant="npi-primary"
                                            onClick={() => {
                                                setOpenSuspendModal(
                                                    currentStatus === PartnerStatuses.ACTIVE
                                                );
                                            }}
                                        >
                                            Suspend
                                        </Button>
                                    );
                                } else if (currentStatus === PartnerStatuses.SUSPENDED) {
                                    return (
                                        <Button
                                            startIcon={<RestoreIcon />}
                                            variant="npi-primary"
                                            onClick={() => {
                                                setIsConfirmationModalOpen(
                                                    currentStatus === PartnerStatuses.SUSPENDED
                                                );
                                            }}
                                        >
                                            Unsuspend
                                        </Button>
                                    );
                                }
                            })()}
                        </Box>
                    ) : null}
                </>
            )}

            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                title="Unsuspend Partner"
                message={`
        Are you sure you want to unsuspend, ${partnerName}? \n Unsuspending this partner will not unsuspend their corresponding user accounts. These will also need to be unsuspended.
        `}
                onCancel={() => {
                    setIsConfirmationModalOpen(false);
                }}
                onConfirm={() => {
                    handleStatusChange(PartnerStatuses.ACTIVE);
                    setIsConfirmationModalOpen(false);
                }}
            />

            <SuspendModal
                handleSuspendPartner={handleSuspendPartner}
                isModalOpen={openSuspendModal}
                setIsModalOpen={setOpenSuspendModal}
                setSuspendReason={setSuspendReason}
                suspendReason={suspendReason}
            />
        </Box>
    );
};

export default Status;
